import $ from 'jquery';
import _ from 'underscore';
import { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { ListboxSelect, LinkButton } from '@evertrue/et-components';
import Icon from 'components/elements/icon';
import KeyPressManager from 'components/helpers/KeyPressManager';
import FeatureStore from 'apps/layout/stores/feature-store';
import PropTypes from 'prop-types';
import { useFluxStore } from '@evertrue/et-flux';

const PROXIMITY_SORTS = ['addresses.location', 'proximity_sort'];

const hasScoresMapState = () => ({ hasScores: FeatureStore.hasFeature('scores') });

const SortDropdown = ({ options, sortProp, sortReverse = false, onSort, className }) => {
  const { hasScores } = useFluxStore(FeatureStore, hasScoresMapState);

  const filterOptions = useMemo(() => {
    const matcher = hasScores ? 'et_donor_index' : 'score';
    return _.filter(options, (option) => !option.value?.match(matcher));
  }, [options, hasScores]);

  const handleSortReverse = useCallback(
    (e) => {
      const reversed = !sortReverse;
      onSort(sortProp, reversed);
      if (e?.currentTarget) $(e.currentTarget).blur();
    },
    [sortProp, sortReverse, onSort]
  );

  const valueObject = useMemo(() => {
    return _.findWhere(filterOptions, { value: PROXIMITY_SORTS.includes(sortProp) ? 'proximity_sort' : sortProp });
  }, [filterOptions, sortProp]);

  if (filterOptions.length === 1) {
    return (
      <>
        <div role="status" aria-live="polite" aria-atomic="true" className="visually-hidden">
          {`Currently sorted in ${sortReverse ? 'ascending' : 'descending'} order.`}
        </div>
        <KeyPressManager
          onEnter={handleSortReverse}
          children={({ onKeyDown, ref }) => (
            <LinkButton
              ref={ref}
              onKeyDown={onKeyDown}
              title="Click to reverse the sort"
              className={classNames(className, 'sort-dropdown--single')}
              onClick={handleSortReverse}
              role="button"
              aria-label={`Reverse sort. Currently sorted in ${sortReverse ? 'ascending' : 'descending'} order.`}
            >
              <Icon
                className={classNames('sort-dropdown--icon', { 'is-selected': !sortReverse })}
                icon="long-arrow-down"
                aria-label="Sort descending"
              />
              <Icon
                className={classNames('sort-dropdown--icon', { 'is-selected': !!sortReverse })}
                icon="long-arrow-up"
                aria-label="Sort ascending"
              />
              {_.first(filterOptions)?.label}
            </LinkButton>
          )}
        />
      </>
    );
  }

  return (
    <div className={classNames('sort-dropdown', className)}>
      <div role="status" aria-live="polite" aria-atomic="true" className="visually-hidden">
        {`Currently sorted in ${sortReverse ? 'ascending' : 'descending'} order.`}
      </div>
      <KeyPressManager
        onEnter={handleSortReverse}
        children={({ onKeyDown, ref }) => (
          <LinkButton
            ref={ref}
            onKeyDown={onKeyDown}
            title="Reverse the sort"
            className="sort-dropdown--btn"
            onClick={handleSortReverse}
            role="button"
            aria-label={`Reverse sort. Currently sorted in ${sortReverse ? 'ascending' : 'descending'} order.`}
          >
            <Icon
              className={classNames('sort-dropdown--icon', { 'is-selected': !sortReverse })}
              icon="long-arrow-down"
              aria-label="Sort descending"
            />
            <Icon
              className={classNames('sort-dropdown--icon', { 'is-selected': !!sortReverse })}
              icon="long-arrow-up"
              aria-label="Sort ascending"
            />
          </LinkButton>
        )}
      />
      <ListboxSelect
        value={valueObject}
        options={filterOptions}
        label="sorting options"
        onChange={(selected) => onSort(selected?.value, sortReverse)}
      />
    </div>
  );
};

export default SortDropdown;

SortDropdown.propTypes = {
  options: PropTypes.array,
  sortProp: PropTypes.string,
  sortReverse: PropTypes.bool,
  onSort: PropTypes.func,
  className: PropTypes.string,
};
